import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Film,
  Award,
  Users,
  Map,
  Camera,
  Edit,
  VideoIcon,
  ThumbsUp,
  Apple,
  PlusCircle,
  User,
  Layers,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import "./Tracker.css";

import fetchFromAPI from "../utils/fetchFromAPI";

const Tracker = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("person"); // "person" or "frame"
  const [selectedFrame, setSelectedFrame] = useState("all");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [people, setPeople] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  // State to track expanded film lists
  const [expandedLists, setExpandedLists] = useState({});

  console.log("Current expandedLists state:", expandedLists);

  // Frame definitions with their requirements
  const frameDefinitions = {
    directing: {
      name: "Directing Frame",
      threshold: 5,
      icon: <Film size={24} />,
    },
    writing: { name: "Writing Frame", threshold: 4, icon: <Edit size={24} /> },
    ep: { name: "EP Frame", threshold: 6, icon: <ThumbsUp size={24} /> },
    editing: {
      name: "Editing Frame",
      threshold: 4,
      icon: <VideoIcon size={24} />,
    },
    acting: { name: "Acting Frame", threshold: 6, icon: <Users size={24} /> },
    cinematography: {
      name: "Cinematography Frame",
      threshold: 4,
      icon: <Camera size={24} />,
    },
    interborough: {
      name: "Interborough Frame",
      threshold: 5,
      icon: <Map size={24} />,
    }, // One film in each borough
    producer: {
      name: "Producer Frame",
      threshold: 6,
      icon: <PlusCircle size={24} />,
    },
    bigApple: {
      name: "Big Apple Frame",
      threshold: 8,
      icon: <Apple size={24} />,
    }, // All other frames
  };

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        // Use the new public tracker endpoint instead of submissionsform
        const response = await fetchFromAPI("/api/tracker");
        setSubmissions(response.data);

        // Extract unique people from all roles
        const allPeople = new Set();

        response.data.forEach((submission) => {
          // Process all roles from each submission
          [
            ...(submission.directors || []),
            ...(submission.writers || []),
            ...(submission.editors || []),
            ...(submission.dps || []),
            ...(submission.producers || []),
            ...(submission.actors || []),
            ...(submission.executiveProducers || []),
          ].forEach((person) => {
            if (person && typeof person === "string") {
              allPeople.add(person);
            }
          });
        });

        // Create people array with their stats
        const peopleArray = Array.from(allPeople).map((name) => {
          return {
            name,
            stats: calculatePersonStats(name, response.data),
          };
        });

        // Sort people by total contributions
        peopleArray.sort(
          (a, b) => b.stats.totalContributions - a.stats.totalContributions
        );

        setPeople(peopleArray);
        setSelectedPerson(peopleArray.length > 0 ? peopleArray[0].name : null);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching submissions:", error);
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, []);

  // Calculate a person's stats across all frames
  const calculatePersonStats = (personName, allSubmissions) => {
    const stats = {
      totalContributions: 0,
      directing: { count: 0, films: [], complete: false },
      writing: { count: 0, films: [], complete: false },
      ep: { count: 0, films: [], complete: false },
      editing: { count: 0, films: [], complete: false },
      acting: { count: 0, films: [], complete: false },
      cinematography: { count: 0, films: [], complete: false },
      producer: { count: 0, films: [], complete: false },
      interborough: { boroughs: new Set(), films: [], complete: false },
      bigApple: { complete: false },
    };

    allSubmissions.forEach((submission) => {
      // Directing
      if (submission.directors && submission.directors.includes(personName)) {
        stats.directing.count++;
        stats.directing.films.push(submission.filmTitle);
        stats.totalContributions++;
      }

      // Writing
      if (submission.writers && submission.writers.includes(personName)) {
        stats.writing.count++;
        stats.writing.films.push(submission.filmTitle);
        stats.totalContributions++;
      }

      // EP
      if (
        submission.executiveProducers &&
        submission.executiveProducers.includes(personName)
      ) {
        stats.ep.count++;
        stats.ep.films.push(submission.filmTitle);
        stats.totalContributions++;
      }

      // Editing
      if (submission.editors && submission.editors.includes(personName)) {
        stats.editing.count++;
        stats.editing.films.push(submission.filmTitle);
        stats.totalContributions++;
      }

      // Acting
      if (submission.actors && submission.actors.includes(personName)) {
        stats.acting.count++;
        stats.acting.films.push(submission.filmTitle);
        stats.totalContributions++;
      }

      // Cinematography
      if (submission.dps && submission.dps.includes(personName)) {
        stats.cinematography.count++;
        stats.cinematography.films.push(submission.filmTitle);
        stats.totalContributions++;
      }

      // Producer
      if (submission.producers && submission.producers.includes(personName)) {
        stats.producer.count++;
        stats.producer.films.push(submission.filmTitle);
        stats.totalContributions++;
      }

      // Interborough - check boroughs
      // Interborough - check boroughs (only films they directed)
      if (submission.directors && submission.directors.includes(personName)) {
        if (submission.borough) {
          stats.interborough.boroughs.add(submission.borough);
          stats.interborough.films.push({
            title: submission.filmTitle,
            borough: submission.borough,
          });
        }
      }
    });

    // Check if frames are complete
    stats.directing.complete =
      stats.directing.count >= frameDefinitions.directing.threshold;
    stats.writing.complete =
      stats.writing.count >= frameDefinitions.writing.threshold;
    stats.ep.complete = stats.ep.count >= frameDefinitions.ep.threshold;
    stats.editing.complete =
      stats.editing.count >= frameDefinitions.editing.threshold;
    stats.acting.complete =
      stats.acting.count >= frameDefinitions.acting.threshold;
    stats.cinematography.complete =
      stats.cinematography.count >= frameDefinitions.cinematography.threshold;
    stats.producer.complete =
      stats.producer.count >= frameDefinitions.producer.threshold;
    stats.interborough.complete =
      stats.interborough.boroughs.size >=
      frameDefinitions.interborough.threshold;

    // Big Apple (all frames complete)
    stats.bigApple.complete =
      stats.directing.complete &&
      stats.writing.complete &&
      stats.ep.complete &&
      stats.editing.complete &&
      stats.acting.complete &&
      stats.cinematography.complete &&
      stats.producer.complete &&
      stats.interborough.complete;

    // Calculate completed frames count
    const completedFramesCount = [
      stats.directing.complete,
      stats.writing.complete,
      stats.ep.complete,
      stats.editing.complete,
      stats.acting.complete,
      stats.cinematography.complete,
      stats.producer.complete,
      stats.interborough.complete,
    ].filter(Boolean).length;

    stats.completedFramesCount = completedFramesCount;

    return stats;
  };

  // Get all people who have made progress on a specific frame
  const getPeopleByFrame = (frameType) => {
    if (!frameType || frameType === "all") return people;

    return people
      .filter((person) => {
        if (frameType === "bigApple") {
          return person.stats.completedFramesCount > 0;
        }
        if (frameType === "interborough") {
          // Fix: Check boroughs.size instead of count for interborough
          return person.stats[frameType].boroughs.size > 0;
        }
        return person.stats[frameType].count > 0;
      })
      .sort((a, b) => {
        if (frameType === "bigApple") {
          return b.stats.completedFramesCount - a.stats.completedFramesCount;
        }
        if (frameType === "interborough") {
          return (
            b.stats[frameType].boroughs.size - a.stats[frameType].boroughs.size
          );
        }
        return b.stats[frameType].count - a.stats[frameType].count;
      });
  };

  // Calculate progress percentage
  const calculateProgress = (count, threshold) => {
    return Math.min((count / threshold) * 100, 100);
  };

  // Handle click on "more" button - standalone function
  const handleShowMore = (personName, frameType, e) => {
    e.preventDefault();
    e.stopPropagation();

    console.log(`Show more clicked for ${personName}, ${frameType}`);

    const key = `${personName}-${frameType}`;
    setExpandedLists((prev) => {
      const newState = {
        ...prev,
        [key]: true,
      };
      console.log("Updated expandedLists state:", newState);
      return newState;
    });
  };

  // Handle click on "show less" button - standalone function
  const handleShowLess = (personName, frameType, e) => {
    e.preventDefault();
    e.stopPropagation();

    console.log(`Show less clicked for ${personName}, ${frameType}`);

    const key = `${personName}-${frameType}`;
    setExpandedLists((prev) => {
      const newState = {
        ...prev,
        [key]: false,
      };
      console.log("Updated expandedLists state:", newState);
      return newState;
    });
  };

  // Check if a film list is expanded
  const isFilmListExpanded = (personName, frameType) => {
    const key = `${personName}-${frameType}`;
    return expandedLists[key] || false;
  };

  // Filter people based on search term
  const filteredPeople = people.filter((person) =>
    person.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get people for the selected frame
  const peopleForFrame = getPeopleByFrame(selectedFrame);

  // Filter the frame-specific people by search term
  const filteredPeopleForFrame = peopleForFrame.filter((person) =>
    person.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="tracker-loading">
        <div className="spinner"></div>
        <p>Loading tracker data...</p>
      </div>
    );
  }

  return (
    <div className="tracker-container">
      <div className="tracker-header">
        <Award size={35} className="tracker-icon" />
        <h1>Short Cuts NYC Frames Tracker</h1>
      </div>

      <div className="view-toggle">
        <button
          className={`view-button ${viewMode === "person" ? "active" : ""}`}
          onClick={() => setViewMode("person")}
        >
          <User size={18} />
          <span>Person View</span>
        </button>
        <button
          className={`view-button ${viewMode === "frame" ? "active" : ""}`}
          onClick={() => setViewMode("frame")}
        >
          <Layers size={18} />
          <span>Frame View</span>
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {viewMode === "person" ? (
        <div className="person-view">
          <div className="people-list">
            {filteredPeople.length > 0 ? (
              filteredPeople.map((person) => (
                <div
                  key={person.name}
                  className={`person-card ${selectedPerson === person.name ? "selected" : ""
                    }`}
                  onClick={() => setSelectedPerson(person.name)}
                >
                  <div className="person-avatar">
                    {person.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="person-info">
                    <div className="person-name">{person.name}</div>
                    <div className="person-stats">
                      <span>
                        {person.stats.totalContributions} contributions
                      </span>
                      <span className="dot">•</span>
                      <span>{person.stats.completedFramesCount} frames</span>
                    </div>
                  </div>
                  {person.stats.bigApple.complete && (
                    <div className="big-apple-badge" title="Big Apple Award">
                      <Apple size={20} />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-results">No people match your search.</div>
            )}
          </div>

          {selectedPerson && (
            <div className="person-details">
              <div className="person-header">
                <div className="person-large-avatar">
                  {selectedPerson.charAt(0).toUpperCase()}
                </div>
                <h2>{selectedPerson}</h2>
              </div>

              <div className="frames-grid">
                {Object.entries(frameDefinitions).map(([key, frame]) => {
                  const person = people.find((p) => p.name === selectedPerson);
                  if (!person) return null;

                  const stats = person.stats[key];

                  // Skip Big Apple frame if not viewing a specific person's complete details
                  if (key === "bigApple" && !selectedPerson) return null;

                  let count, threshold, progress;

                  if (key === "interborough") {
                    count = stats.boroughs.size;
                    threshold = frame.threshold;
                    progress = calculateProgress(count, threshold);
                  } else if (key === "bigApple") {
                    count = person.stats.completedFramesCount;
                    threshold = 8; // All other frames
                    progress = calculateProgress(count, threshold);
                  } else {
                    count = stats.count;
                    threshold = frame.threshold;
                    progress = calculateProgress(count, threshold);
                  }

                  const isComplete =
                    key === "bigApple" ? stats.complete : count >= threshold;
                  const isExpanded = isFilmListExpanded(person.name, key);

                  return (
                    <div
                      key={key}
                      className={`frame-card ${isComplete ? "complete" : ""}`}
                    >
                      <div className="frame-header">
                        <span className="frame-icon">{frame.icon}</span>
                        <h3>{frame.name}</h3>
                      </div>

                      <div className="progress-container">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <div className="progress-text">
                          {key === "interborough" ? (
                            <>
                              <span>
                                {Array.from(stats.boroughs).join(", ") ||
                                  "None"}
                              </span>
                              <span className="progress-count">
                                {count}/{threshold} boroughs
                              </span>
                            </>
                          ) : key === "bigApple" ? (
                            <span className="progress-count">
                              {count}/{threshold} frames complete
                            </span>
                          ) : (
                            <span className="progress-count">
                              {count}/{threshold} films
                            </span>
                          )}
                        </div>
                      </div>

                      {key !== "bigApple" && (
                        <div className="frame-films">
                          {key === "interborough" ? (
                            <div className="films-list">
                              {(isExpanded
                                ? stats.films
                                : stats.films.slice(0, 3)
                              ).map((film, index) => (
                                <div key={index} className="film-item">
                                  <span className="film-title">
                                    {film.title}
                                  </span>
                                  <span className="film-info">
                                    {film.borough}
                                  </span>
                                </div>
                              ))}

                              {!isExpanded && stats.films.length > 3 && (
                                <a
                                  href="#"
                                  className="show-more-link"
                                  onClick={(e) =>
                                    handleShowMore(person.name, key, e)
                                  }
                                  style={{
                                    display: "block",
                                    textAlign: "center",
                                    padding: "10px",
                                    margin: "10px 0",
                                    background: "rgba(58, 83, 97, 0.8)",
                                    color: "#eb8876",
                                    borderRadius: "6px",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    transition: "background-color 0.2s",
                                    zIndex: 10,
                                  }}
                                >
                                  +{stats.films.length - 3} more films
                                </a>
                              )}

                              {isExpanded && stats.films.length > 3 && (
                                <a
                                  href="#"
                                  className="show-less-link"
                                  onClick={(e) =>
                                    handleShowLess(person.name, key, e)
                                  }
                                  style={{
                                    display: "block",
                                    textAlign: "center",
                                    padding: "10px",
                                    margin: "10px 0",
                                    background: "rgba(235, 136, 118, 0.2)",
                                    color: "#d1c5b2",
                                    borderRadius: "6px",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    transition: "background-color 0.2s",
                                    zIndex: 10,
                                  }}
                                >
                                  Show less
                                </a>
                              )}
                            </div>
                          ) : (
                            <div className="films-list">
                              {(isExpanded
                                ? stats.films
                                : stats.films.slice(0, 3)
                              ).map((film, index) => (
                                <div key={index} className="film-item">
                                  <span className="film-title">{film}</span>
                                </div>
                              ))}

                              {!isExpanded && stats.films.length > 3 && (
                                <a
                                  href="#"
                                  className="show-more-link"
                                  onClick={(e) =>
                                    handleShowMore(person.name, key, e)
                                  }
                                  style={{
                                    display: "block",
                                    textAlign: "center",
                                    padding: "10px",
                                    margin: "10px 0",
                                    background: "rgba(58, 83, 97, 0.8)",
                                    color: "#eb8876",
                                    borderRadius: "6px",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    transition: "background-color 0.2s",
                                    zIndex: 10,
                                  }}
                                >
                                  +{stats.films.length - 3} more films
                                </a>
                              )}

                              {isExpanded && stats.films.length > 3 && (
                                <a
                                  href="#"
                                  className="show-less-link"
                                  onClick={(e) =>
                                    handleShowLess(person.name, key, e)
                                  }
                                  style={{
                                    display: "block",
                                    textAlign: "center",
                                    padding: "10px",
                                    margin: "10px 0",
                                    background: "rgba(235, 136, 118, 0.2)",
                                    color: "#d1c5b2",
                                    borderRadius: "6px",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    transition: "background-color 0.2s",
                                    zIndex: 10,
                                  }}
                                >
                                  Show less
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="frame-view">
          <div className="frame-selector">
            <div
              className={`frame-tab ${selectedFrame === "all" ? "active" : ""}`}
              onClick={() => setSelectedFrame("all")}
            >
              All Frames
            </div>
            {Object.entries(frameDefinitions).map(([key, frame]) => (
              <div
                key={key}
                className={`frame-tab ${selectedFrame === key ? "active" : ""}`}
                onClick={() => setSelectedFrame(key)}
              >
                <span className="frame-tab-icon">{frame.icon}</span>
                <span className="frame-tab-name">{frame.name}</span>
              </div>
            ))}
          </div>

          <div className="frame-people-list">
            <h2>
              {selectedFrame === "all"
                ? "All Participants"
                : `${frameDefinitions[selectedFrame]?.name || "Frame"
                } Progress`}
            </h2>

            {filteredPeopleForFrame.length > 0 ? (
              <div className="progress-people-grid">
                {filteredPeopleForFrame.map((person) => {
                  let count, threshold, progress;

                  if (selectedFrame === "all") {
                    count = person.stats.totalContributions;
                    threshold = 50; // Arbitrary number for "all" view
                    progress = Math.min((count / threshold) * 100, 100);
                  } else if (selectedFrame === "interborough") {
                    count = person.stats[selectedFrame].boroughs.size;
                    threshold = frameDefinitions[selectedFrame].threshold;
                    progress = calculateProgress(count, threshold);
                  } else if (selectedFrame === "bigApple") {
                    count = person.stats.completedFramesCount;
                    threshold = 8; // All other frames
                    progress = calculateProgress(count, threshold);
                  } else {
                    count = person.stats[selectedFrame].count;
                    threshold = frameDefinitions[selectedFrame].threshold;
                    progress = calculateProgress(count, threshold);
                  }

                  const isComplete =
                    selectedFrame === "all"
                      ? person.stats.bigApple.complete
                      : selectedFrame === "bigApple"
                        ? person.stats.bigApple.complete
                        : count >= threshold;

                  const isExpanded = isFilmListExpanded(
                    person.name,
                    selectedFrame
                  );

                  return (
                    <div
                      key={person.name}
                      className={`person-progress-card ${isComplete ? "complete" : ""
                        }`}
                    >
                      <div className="person-progress-header">
                        <div className="person-progress-avatar">
                          {person.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="person-progress-info">
                          <div className="person-progress-name">
                            {person.name}
                          </div>
                          {selectedFrame === "interborough" ? (
                            <div className="person-progress-count">
                              {count}/{threshold} boroughs
                            </div>
                          ) : selectedFrame === "bigApple" ? (
                            <div className="person-progress-count">
                              {count}/{threshold} frames
                            </div>
                          ) : selectedFrame === "all" ? (
                            <div className="person-progress-count">
                              {person.stats.completedFramesCount} frames
                              complete
                            </div>
                          ) : (
                            <div className="person-progress-count">
                              {count}/{threshold} films
                            </div>
                          )}
                        </div>
                        {isComplete && (
                          <div className="completion-badge">
                            <Award size={20} />
                          </div>
                        )}
                      </div>

                      <div className="person-progress-bar">
                        <div
                          className="person-progress-fill"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>

                      {selectedFrame !== "all" &&
                        selectedFrame !== "bigApple" && (
                          <div className="person-frame-details">
                            {selectedFrame === "interborough" ? (
                              <div className="boroughs-list">
                                {Array.from(
                                  person.stats.interborough.boroughs
                                ).map((borough, index) => (
                                  <span key={index} className="borough-tag">
                                    {borough}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="films-preview">
                                {(isExpanded
                                  ? person.stats[selectedFrame].films
                                  : person.stats[selectedFrame].films.slice(
                                    0,
                                    2
                                  )
                                ).map((film, index) => (
                                  <div
                                    key={index}
                                    className="film-preview-item"
                                  >
                                    {film}
                                  </div>
                                ))}

                                {!isExpanded &&
                                  person.stats[selectedFrame].films.length >
                                  2 && (
                                    <a
                                      href="#"
                                      className="show-more-link"
                                      onClick={(e) =>
                                        handleShowMore(
                                          person.name,
                                          selectedFrame,
                                          e
                                        )
                                      }
                                      style={{
                                        display: "block",
                                        textAlign: "center",
                                        padding: "10px",
                                        margin: "10px 0",
                                        background: "rgba(58, 83, 97, 0.8)",
                                        color: "#eb8876",
                                        borderRadius: "6px",
                                        textDecoration: "none",
                                        cursor: "pointer",
                                        fontWeight: "bold",
                                        transition: "background-color 0.2s",
                                        zIndex: 10,
                                      }}
                                    >
                                      +
                                      {person.stats[selectedFrame].films
                                        .length - 2}{" "}
                                      more films
                                    </a>
                                  )}

                                {isExpanded &&
                                  person.stats[selectedFrame].films.length >
                                  2 && (
                                    <a
                                      href="#"
                                      className="show-less-link"
                                      onClick={(e) =>
                                        handleShowLess(
                                          person.name,
                                          selectedFrame,
                                          e
                                        )
                                      }
                                      style={{
                                        display: "block",
                                        textAlign: "center",
                                        padding: "10px",
                                        margin: "10px 0",
                                        background: "rgba(235, 136, 118, 0.2)",
                                        color: "#d1c5b2",
                                        borderRadius: "6px",
                                        textDecoration: "none",
                                        cursor: "pointer",
                                        fontWeight: "bold",
                                        transition: "background-color 0.2s",
                                        zIndex: 10,
                                      }}
                                    >
                                      Show less
                                    </a>
                                  )}
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-results">
                No people match your search for this frame.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tracker;
